import * as React from 'react'

import moment from 'moment'

import { WeeklyReducedRestEventModel } from '@/app/models/weeklyRegularRestModel'
import { WeeklyRestEventModel } from '@/app/models/weeklyRestModel'
import { DATE_FORMAT } from '@/app/constants/date'

import { useScene } from '../context/SceneContext'

const color = '#5B7F00'

export type RestReducedPartBlockProps = {
  weeklyReducedRest: WeeklyReducedRestEventModel
  index: number
}

const prepareTooltipInfo = (
  weeklyReducedRest: WeeklyReducedRestEventModel,
): WeeklyRestEventModel => ({
  ...(weeklyReducedRest as any),
  typeName: 'eventName.weeklyRest',
  color,
})

const ReducedArrowIcon: React.FC<{
  left: number
  height: number
  onTheRight: boolean
  tooltip: string
}> = ({ left, height, onTheRight, tooltip }) => {
  const image = onTheRight ? 'rightArrowGreen' : 'leftArrowGreen'
  return (
    <div
      className="tw-flex tw-flex-col tw-items-start tw-justify-start"
      style={{ position: 'absolute', left, top: 15, bottom: 0 }}
    >
      <div
        style={{
          borderColor: '#000000',
          width: 30,
          height: height / 4,
          pointerEvents: 'auto',
        }}
        data-tip={tooltip}
        data-for="weeklyRestTooltip"
      >
        <img
          src={`/assets/icons_nucleo/icons/mix/${image}.svg`}
          width={20}
          height={15}
        />
      </div>
    </div>
  )
}

export const RestReducedPartBlock: React.FC<RestReducedPartBlockProps> =
  React.memo((props: RestReducedPartBlockProps) => {
    const scene = useScene()
    const HEIGHT = scene.height - 54
    const weeklyRest = props.weeklyReducedRest

    const startTime = scene.startTime

    if (scene.days > 1) {
      const tooltip = JSON.stringify(prepareTooltipInfo(weeklyRest))
      const arrows: React.ReactNode[] = []

      for (let d = 0; d < scene.days; d++) {
        const dayStart = startTime.clone().add(d, 'days')
        const dayEnd = dayStart.clone().add(1, 'days')
        const weekAfterDay = dayStart.clone().add(7, 'days')

        const overlaps =
          moment(weeklyRest.startTime).isBefore(dayEnd) &&
          moment(weeklyRest.endTime).isAfter(dayStart)
        if (!overlaps) continue

        const endsWithinWeek = moment(weeklyRest.realRestEndTime).isBefore(
          weekAfterDay,
        )
        const isStartDay =
          moment(weeklyRest.startTime).isAfter(dayStart) && endsWithinWeek
        const isFdowDay =
          endsWithinWeek &&
          moment(weeklyRest.firstDayOfWeekTheRestBelongsTo).format(
            DATE_FORMAT,
          ) === moment(dayStart).format(DATE_FORMAT)
        if (isStartDay || isFdowDay) continue

        const onTheRight =
          moment(weeklyRest.firstDayOfWeekTheRestBelongsTo) > moment(dayStart)

        arrows.push(
          <ReducedArrowIcon
            key={d}
            left={scene.timeToX(dayStart) - scene.gutter}
            height={HEIGHT}
            onTheRight={onTheRight}
            tooltip={tooltip}
          />,
        )
      }

      return <>{arrows}</>
    }

    const oneWeek = startTime.clone().add(7, 'days')
    const twoWeeks = startTime.clone().add(14, 'days')

    let leftTime: any = weeklyRest.startTime

    if (
      moment(weeklyRest.startTime) > moment(startTime) &&
      moment(weeklyRest.realRestEndTime) < moment(oneWeek)
    ) {
      return null
    }

    if (
      moment(weeklyRest.startTime) > moment(oneWeek) &&
      moment(weeklyRest.realRestEndTime) < moment(twoWeeks)
    ) {
      return null
    }

    if (
      moment(weeklyRest.realRestEndTime) < moment(twoWeeks) &&
      moment(weeklyRest.realRestEndTime) > moment(oneWeek) &&
      moment(weeklyRest.startTime) < moment(oneWeek)
    ) {
      leftTime = oneWeek.clone().add(-6, 'hours')
    }
    if (
      moment(weeklyRest.realRestEndTime) > moment(twoWeeks) &&
      moment(weeklyRest.realRestEndTime) > moment(oneWeek) &&
      moment(weeklyRest.startTime) > moment(oneWeek)
    ) {
      leftTime = twoWeeks.clone().add(-6, 'hours')
    }

    if (
      moment(weeklyRest.realRestEndTime) < moment(oneWeek) &&
      moment(weeklyRest.firstDayOfWeekTheRestBelongsTo).format(DATE_FORMAT) ==
        moment(startTime).format(DATE_FORMAT)
    ) {
      return null
    }

    if (
      moment(weeklyRest.realRestEndTime) > moment(oneWeek) &&
      moment(weeklyRest.realRestEndTime) < moment(twoWeeks) &&
      moment(weeklyRest.firstDayOfWeekTheRestBelongsTo).format(DATE_FORMAT) ==
        moment(startTime).format(DATE_FORMAT)
    ) {
      return null
    }

    if (
      moment(weeklyRest.realRestEndTime) > moment(twoWeeks) &&
      moment(weeklyRest.firstDayOfWeekTheRestBelongsTo).format(DATE_FORMAT) ==
        moment(oneWeek).format(DATE_FORMAT)
    ) {
      return null
    }

    const left = scene.timeToX(leftTime) - scene.gutter
    const onTheRight =
      moment(weeklyRest.firstDayOfWeekTheRestBelongsTo) > moment(startTime)
    const image = onTheRight ? 'rightArrowGreen' : 'leftArrowGreen'

    return (
      <div
        className="tw-flex tw-flex-col tw-items-start tw-justify-start"
        style={{ position: 'absolute', left, top: 15, bottom: 0 }}
      >
        <div
          style={{
            borderColor: '#000000',
            width: 30,
            height: HEIGHT / 4,
            pointerEvents: 'auto',
          }}
          data-tip={JSON.stringify(prepareTooltipInfo(weeklyRest))}
          data-for="weeklyRestTooltip"
        >
          <img
            src={`/assets/icons_nucleo/icons/mix/${image}.svg`}
            width={20}
          height={15}
          />
        </div>
      </div>
    )
  })

RestReducedPartBlock.displayName = 'RestReducedPartBlock'
